import { NavBar } from 'components/NavBar/NavBar'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { format, subDays } from 'date-fns'
import { RootState } from 'store'
import { Flex, MainHeadline, PageFooter, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { Pagination } from 'components/Pagination/Pagination'
import { formatNumbersForDisplay, formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { NoData } from 'components/NoData'
import useQueryPagination from 'hooks/useQueryPagination'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { clearTanks, getFuelTanks } from 'features/fuelManagement/fuelManagementSlice'
import {
    createFuelTransfer,
    getBowserReconciliations,
    getFuelCustodyConfig,
    getFuelCustodyPricing,
    getFuelReconciliations,
    saveFuelDip,
    signOffReconciliation,
} from 'features/fuelManagement/fuelCustodySlice'
import { TANK_CLASS_TO_UI } from 'pages/FuelManagement/Shared/fuelEnumMaps'
import { mapTankHousekeeping } from 'pages/FuelManagement/Shared/fuelApiMappers'
import {
  FUEL_COVER_THRESHOLD_TITLE,
  FUEL_THRESHOLD_FOOTNOTE,
} from 'pages/FuelManagement/Shared/fuelFormatters'
import {
  computeBowserRecon,
  computeFuelRecon,
  FUEL_BOWSER_TOLERANCE_PCT,
  FUEL_COVER_DANGER_DAYS,
  FUEL_COVER_WARNING_DAYS,
  FUEL_VARIANCE_TOLERANCE_PCT,
  fuelReconDateISO,
  fuelReconDateLabel,
} from 'pages/FuelManagement/Shared/fuelReconUtils'
import { BannerText, BannerWrap } from 'pages/Operations/Shared/opsShared.styled'
import { ISelectValue } from 'constants/globalTypes'
import { IStatusPill } from 'types/ui/statusPill.interface'
import {
    IFuelDipSavePayload,
    IFuelReconSignOffMap,
} from 'types/fuelReconciliation.interface'
import {
    IFuelBowserHistoryRow,
    IFuelBowserReconComputed,
    IFuelCustodyReconComputed,
    IFuelCustodyReconDay,
    IFuelCustodyTankVM,
    IFuelHousekeepingRow,
    IFuelLocalTransfer,
    IFuelReconCustodyHistoryRow,
    IFuelTransferLine,
    IFuelTransferSavePayload,
} from 'types/fuelCustody.interface'
import { FuelTankClass } from 'types/fuelEnums.interface'
import { TanksModal } from './TanksModal'
import { EnterDipModal } from './EnterDipModal'
import { TransferModal } from './TransferModal'
import { TankLevelBar } from './TankLevelBar'
import { DispensersPanel } from './DispensersPanel'
import { MonthEndPanel } from './MonthEndPanel'
import { BowserDayBreakdown } from './BowserDayBreakdown'
import {
    BreachNoteText,
    CardNote,
    CoverValue,
    FootnoteLine,
    LedgerLabel,
    LedgerNote,
    LedgerOp,
    LedgerRow,
    LedgerValue,
    LedgerWrap,
    LinkText,
    SignedText,
    SignOffWrap,
    TankCard,
    TankInfo,
    TankMetricLabel,
    TankMetricsGrid,
    TankMetricValue,
    TankName,
    TanksGrid,
    TankTopRow,
    TotalsLine,
    VarianceRow,
    WaterNote,
} from './FuelTanks.styled'

const fmt = (value: number): string => formatNumbersForDisplay(value)

const fmtDays = (days: number | null): string => {
    if (days == null) return '—'
    return Number.isInteger(days) ? `${days} days` : `${days.toFixed(1)} days`
}

const signedLitres = (value: number): string =>
    `${value > 0 ? '+' : value < 0 ? '−' : ''}${fmt(Math.abs(value))}`

const signedPct = (value: number): string =>
    `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value).toFixed(1)}%`

const toVM = (tank: any, wacPerLitre: number, coverDangerDays: number, coverWarningDays: number): IFuelCustodyTankVM => {
    const machineClass: 'STATIC' | 'BOWSER' =
        tank.machineClass === FuelTankClass.Bowser || tank.machineClass === 'BOWSER'
            ? 'BOWSER'
            : tank.machineClass != null && TANK_CLASS_TO_UI[tank.machineClass]
              ? TANK_CLASS_TO_UI[tank.machineClass]
              : 'STATIC'
    const burnRatePerDay = tank.burnRatePerDay ?? 0
    const pricePerLitre = wacPerLitre || tank.pricePerLitre || 0
    const pct = tank.capacity > 0 ? Math.round((tank.currentAmount / tank.capacity) * 100) : 0
    const sensorAmount = tank.currentSensorAmount ?? null
    const daysOfCover =
        machineClass === 'STATIC' && burnRatePerDay
            ? Math.round((tank.currentAmount / burnRatePerDay) * 10) / 10
            : null
    const overfull = pct >= 100
    let tone: IStatusPill['status'] = 'success'
    let statusLabel = 'Ok'
    if (machineClass === 'BOWSER') {
        tone = 'info'
        statusLabel = 'Bowser'
    } else if (overfull) {
        tone = 'warning'
        statusLabel = 'Overfull — verify dip & meter'
    } else if (daysOfCover == null) {
        tone = 'neutral'
        statusLabel = 'No burn data'
    } else if (daysOfCover < coverDangerDays) {
        tone = 'danger'
        statusLabel = 'Reorder now'
    } else if (daysOfCover < coverWarningDays) {
        tone = 'warning'
        statusLabel = 'Watch'
    }
    return {
        id: tank.id,
        name: tank.name,
        currentAmount: tank.currentAmount,
        capacity: tank.capacity,
        pct,
        stockValue: tank.currentAmount * pricePerLitre,
        burnRatePerDay,
        daysOfCover,
        tone,
        statusLabel,
        reorderLitres: Math.max(tank.capacity - tank.currentAmount, 0),
        overfull,
        sensorAmount,
        sensorPct: sensorAmount != null && tank.capacity > 0 ? Math.round((sensorAmount / tank.capacity) * 100) : null,
        source: tank,
        machineClass,
        routeMeta: tank.routeMeta ?? null,
        coverNote: tank.coverNote ?? null,
        waterCheck: tank.lastWaterCheck
            ? {
                  dateLabel: formatDateForDisplay(tank.lastWaterCheck.checkedAt),
                  result: tank.lastWaterCheck.result,
                  warning: tank.lastWaterCheck.warning,
              }
            : null,
    }
}

const reconSentence = (c: IFuelCustodyReconComputed): string => {
    if (c.varianceL === 0) return `Book stock says ${fmt(c.bookStockL)} L and the closing dip agrees exactly.`
    const direction = c.varianceL < 0 ? 'short' : 'over'
    const verdict = c.withinTolerance ? 'within tolerance' : 'outside tolerance — explain it before sign-off'
    return `Book stock says ${fmt(c.bookStockL)} L. The closing dip found ${fmt(c.closingDipL)} L — ${fmt(Math.abs(c.varianceL))} L ${direction}, ${verdict}.`
}

const bowserSentence = (c: IFuelBowserReconComputed | null): string => {
    if (!c) return ''
    const direction = c.varianceL < 0 ? 'short' : c.varianceL > 0 ? 'over' : 'exact'
    if (c.varianceL === 0) return `Loaded ${fmt(c.loadedL)} L, dispensed ${fmt(c.dispensedL)} L — the metered return agrees exactly.`
    const verdict = c.withinTolerance ? 'within tolerance' : 'outside tolerance — explain it before sign-off'
    return `Loaded ${fmt(c.loadedL)} L − dispensed ${fmt(c.dispensedL)} L leaves ${fmt(c.expectedReturnL)} L expected back. The meter recorded ${fmt(c.meteredReturnL)} L — ${fmt(Math.abs(c.varianceL))} L ${direction}, ${verdict}.`
}

export const FuelTanks = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [modal, setModal] = useState({ open: false, value: null })
    const [dipModalOpen, setDipModalOpen] = useState(false)
    const [transferModalOpen, setTransferModalOpen] = useState(false)
    const [view, setView] = useState<'tanks' | 'recon' | 'dispensers'>('tanks')
    const [reconMode, setReconMode] = useState<'daily' | 'monthend'>('daily')
    const [reconTankId, setReconTankId] = useState<number | null>(null)
    const [selectedISO, setSelectedISO] = useState(fuelReconDateISO(1))
    const [pickedDate, setPickedDate] = useState<Date | null>(null)
    const [localDays, setLocalDays] = useState<IFuelCustodyReconDay[]>([])
    const [localTransfers, setLocalTransfers] = useState<IFuelLocalTransfer[]>([])
    const [signOffs, setSignOffs] = useState<IFuelReconSignOffMap>({})
    const [params, setParams] = useState<any>(getParams({ location }))
    const { tanks } = useSelector((store: RootState) => store.fuelManagement)
    const { config, pricing, reconciliations, bowserReconciliations } = useSelector(
        (store: RootState) => store.fuelCustody,
    )

    useQueryPagination({ fetchFunctions: [getFuelTanks], cleanUpFunctions: [clearTanks], params })

    useEffect(() => {
        dispatch(getFuelCustodyConfig() as any)
        dispatch(getFuelCustodyPricing() as any)
    }, [dispatch])

    const coverDangerDays = config?.coverDangerDays ?? FUEL_COVER_DANGER_DAYS
    const coverWarningDays = config?.coverWarningDays ?? FUEL_COVER_WARNING_DAYS
    const reconTolerancePct = config?.reconTolerancePct ?? FUEL_VARIANCE_TOLERANCE_PCT
    const bowserTolerancePct = config?.bowserTolerancePct ?? FUEL_BOWSER_TOLERANCE_PCT
    const wacPerLitre = pricing?.wacPerLitre ?? 0

    const sourceTanks: any[] = tanks?.data ?? []
    const vms = sourceTanks.map((t) => toVM(t, wacPerLitre, coverDangerDays, coverWarningDays))
    const staticVms = vms.filter((vm) => vm.machineClass === 'STATIC')
    const bowserVm = vms.find((vm) => vm.machineClass === 'BOWSER') || null

    const totalLitres = staticVms.reduce((sum, vm) => sum + vm.currentAmount, 0)
    const totalValue = staticVms.reduce((sum, vm) => sum + vm.stockValue, 0)
    const totalBurn = staticVms.reduce((sum, vm) => sum + (vm.burnRatePerDay || 0), 0)
    const totalCoverDays = totalBurn > 0 ? totalLitres / totalBurn : null

    const housekeepingRows = useMemo(
        () =>
            sourceTanks
                .map((tank) => mapTankHousekeeping(tank))
                .filter((row): row is IFuelHousekeepingRow => row != null),
        [sourceTanks],
    )

    const activeTankId = reconTankId ?? vms.find((vm) => vm.tone === 'danger')?.id ?? vms[0]?.id ?? null
    const activeTank = vms.find((vm) => vm.id === activeTankId) || null
    const isBowserActive = activeTank?.machineClass === 'BOWSER'

    useEffect(() => {
        if (view !== 'recon' || activeTankId == null) return
        if (isBowserActive) {
            dispatch(
                getBowserReconciliations({
                    id: activeTankId,
                    params: { page: 1, limit: 30 },
                }) as any,
            )
        } else {
            dispatch(
                getFuelReconciliations({
                    page: 1,
                    limit: 30,
                    tankId: activeTankId,
                }) as any,
            )
        }
    }, [dispatch, view, activeTankId, isBowserActive])

    const apiReconForDay = (tankId: number, iso: string) =>
        reconciliations.data.find((r) => r.tankId === tankId && r.dateISO === iso) ?? null

    const apiReconToComputed = (r: NonNullable<ReturnType<typeof apiReconForDay>>): IFuelCustodyReconComputed => ({
        tankId: r.tankId,
        dateISO: r.dateISO,
        openingDipL: r.openingDipL,
        closingDipL: r.closingDipL ?? 0,
        deliveriesL: r.deliveriesL,
        dispensesL: r.dispensesL,
        dispenseCount: r.dispenseCount,
        transfersInL: r.transfersInL,
        transfersOutL: r.transfersOutL,
        transfers: r.transfers.map((t) => ({
            id: t.id,
            direction: t.direction === 1 ? 'TRANSFER_IN' : 'TRANSFER_OUT',
            counterparty: t.counterparty,
            litres: t.litres,
            time: t.time,
            label: t.label,
        })),
        bookStockL: r.bookStockL,
        varianceL: r.varianceL ?? 0,
        variancePct: r.variancePct ?? 0,
        withinTolerance: r.withinTolerance,
    })

    const dayFor = (tankId: number, iso: string): IFuelCustodyReconDay | null =>
        localDays.find((d) => d.tankId === tankId && d.dateISO === iso) ?? null

    const tankDays = (tankId: number): IFuelCustodyReconDay[] =>
        localDays.filter((d) => d.tankId === tankId).sort((a, b) => b.dateISO.localeCompare(a.dateISO))

    const transferLinesFor = (tankId: number, iso: string): IFuelTransferLine[] =>
        localTransfers
            .filter((t) => t.dateISO === iso)
            .flatMap((t) => {
                const lines: IFuelTransferLine[] = []
                if (t.fromTankId === tankId) {
                    lines.push({
                        id: t.id,
                        direction: 'TRANSFER_OUT',
                        counterparty: t.toName,
                        litres: t.litres,
                        time: t.time,
                        label: t.toBowser ? `Transfer out — ${t.toName} load` : `Transfer out — ${t.toName}`,
                    })
                }
                if (t.toTankId === tankId && !t.toBowser) {
                    lines.push({
                        id: t.id + 1,
                        direction: 'TRANSFER_IN',
                        counterparty: t.fromName,
                        litres: t.litres,
                        time: t.time,
                        label: `Transfer in — from ${t.fromName}`,
                    })
                }
                return lines
            })

    const withLocalTransfers = (day: IFuelCustodyReconDay): IFuelCustodyReconDay => {
        const extra = transferLinesFor(day.tankId, day.dateISO)
        return extra.length ? { ...day, transfers: [...(day.transfers ?? []), ...extra] } : day
    }

    const entry = activeTankId != null && !isBowserActive ? dayFor(activeTankId, selectedISO) : null
    const apiRecon = activeTankId != null && !isBowserActive ? apiReconForDay(activeTankId, selectedISO) : null
    const computed = apiRecon
        ? apiReconToComputed(apiRecon)
        : entry
          ? computeFuelRecon(withLocalTransfers(entry), reconTolerancePct)
          : null
    const signKey = `${activeTankId}_${selectedISO}`
    const signOff = apiRecon?.signOff
        ? { by: apiRecon.signOff.by, at: apiRecon.signOff.at }
        : signOffs[signKey]
    const selectedReconId = apiRecon?.id ?? null
    const isSignedOff = apiRecon?.status === 2
    const yesterdayISO = fuelReconDateISO(1)

    const bowserDays = isBowserActive ? bowserReconciliations.data : []
    const bowserApiEntry = isBowserActive
        ? bowserReconciliations.data.find((d) => d.dateISO === selectedISO) ?? null
        : null
    const bowserEntry = bowserApiEntry
        ? {
              bowserId: bowserApiEntry.bowserId,
              dateISO: bowserApiEntry.dateISO,
              loadedL: bowserApiEntry.loadedL,
              loadedFrom: bowserApiEntry.loadedFrom || '',
              loadedTime: bowserApiEntry.loadedTime || '',
              dispensedL: bowserApiEntry.dispensedL,
              machineCount: bowserApiEntry.machineCount,
              meteredReturnL: bowserApiEntry.meteredReturnL,
              note: bowserApiEntry.note || undefined,
          }
        : null
    const bowserComputed = bowserEntry ? computeBowserRecon(bowserEntry, bowserTolerancePct) : null

    const historyRows: IFuelReconCustodyHistoryRow[] =
        activeTankId != null && !isBowserActive
            ? (reconciliations.data.length
                  ? reconciliations.data
                        .filter((r) => r.tankId === activeTankId)
                        .map((r) => ({
                            ...apiReconToComputed(r),
                            id: r.dateISO,
                            dateLabel: fuelReconDateLabel(r.dateISO),
                        }))
                  : tankDays(activeTankId).map((d) => ({
                        ...computeFuelRecon(withLocalTransfers(d), reconTolerancePct),
                        id: d.dateISO,
                        dateLabel: fuelReconDateLabel(d.dateISO),
                    })))
            : []

    const bowserHistoryRows: IFuelBowserHistoryRow[] = bowserDays
        .sort((a, b) => b.dateISO.localeCompare(a.dateISO))
        .map((d) => ({
            ...computeBowserRecon(d, bowserTolerancePct),
            id: d.dateISO,
            dateLabel: fuelReconDateLabel(d.dateISO),
        }))

    const breachToday = isBowserActive
        ? bowserComputed != null && !bowserComputed.withinTolerance
        : computed != null && !computed.withinTolerance

    const dipTankOptions: ISelectValue[] = staticVms.map((vm) => ({ value: vm.id, label: vm.name }))
    const transferTankOptions: ISelectValue[] = vms.map((vm) => ({ value: vm.id, label: vm.name }))

    const selectYesterday = () => {
        setSelectedISO(yesterdayISO)
        setPickedDate(null)
    }

    const handlePickDate = (date: Date | null) => {
        setPickedDate(date)
        if (date) setSelectedISO(format(date, 'yyyy-MM-dd'))
    }

    const handleDipSave = async (payload: IFuelDipSavePayload) => {
        try {
            await dispatch(
                saveFuelDip({
                    tankId: payload.tankId,
                    dateISO: payload.dateISO,
                    openingDipL: payload.openingDipL,
                    closingDipL: payload.closingDipL,
                }) as any,
            )
            await dispatch(
                getFuelReconciliations({ page: 1, limit: 30, tankId: payload.tankId }) as any,
            )
            setReconTankId(payload.tankId)
            setSelectedISO(payload.dateISO)
            setPickedDate(null)
            setView('recon')
        } catch {
            toast.error('Could not save dip')
        }
    }

    const handleSignOff = async () => {
        if (!selectedReconId) {
            toast.error('No reconciliation row to sign off')
            return
        }
        try {
            await dispatch(signOffReconciliation(selectedReconId) as any)
            if (activeTankId) {
                await dispatch(
                    getFuelReconciliations({ page: 1, limit: 30, tankId: activeTankId }) as any,
                )
            }
        } catch {
            toast.error('Could not sign off reconciliation')
        }
    }

    const handleTransferSave = async (payload: IFuelTransferSavePayload) => {
        const movedAt = new Date(`${payload.dateISO}T${payload.time || '12:00'}:00`).toISOString()
        try {
            await dispatch(
                createFuelTransfer({
                    fromTankId: payload.fromTankId,
                    toTankId: payload.toTankId,
                    litres: payload.litres,
                    movedAt,
                    note: payload.note || null,
                }) as any,
            )
            if (activeTankId) {
                await dispatch(
                    getFuelReconciliations({ page: 1, limit: 30, tankId: activeTankId }) as any,
                )
            }
        } catch {
            toast.error('Could not record transfer')
        }
    }

    const historyColumns: IHonestTableColumn<IFuelReconCustodyHistoryRow>[] = [
        {
            id: 'day',
            header: 'Day',
            sticky: true,
            minWidth: '7rem',
            cell: (item) => <LedgerValue $em={item.dateISO === selectedISO}>{item.dateLabel}</LedgerValue>,
        },
        {
            id: 'opening',
            header: 'Opening dip',
            align: 'right',
            cell: (item) => <LedgerValue>{`${fmt(item.openingDipL)} L`}</LedgerValue>,
        },
        {
            id: 'deliveries',
            header: 'Deliveries',
            align: 'right',
            cell: (item) => <LedgerValue>{`${fmt(item.deliveriesL)} L`}</LedgerValue>,
        },
        {
            id: 'dispenses',
            header: 'Dispenses',
            align: 'right',
            cell: (item) => <LedgerValue>{`${fmt(item.dispensesL)} L`}</LedgerValue>,
        },
        {
            id: 'book',
            header: 'Book stock',
            align: 'right',
            cell: (item) => <LedgerValue $em>{`${fmt(item.bookStockL)} L`}</LedgerValue>,
        },
        {
            id: 'closing',
            header: 'Closing dip',
            align: 'right',
            cell: (item) => <LedgerValue>{`${fmt(item.closingDipL)} L`}</LedgerValue>,
        },
        {
            id: 'variance',
            header: 'Variance',
            align: 'right',
            cell: (item) => (
                <StatusPill status={item.withinTolerance ? 'success' : 'danger'}>
                    {`${signedLitres(item.varianceL)} L · ${signedPct(item.variancePct)}`}
                </StatusPill>
            ),
        },
    ]

    const bowserHistoryColumns: IHonestTableColumn<IFuelBowserHistoryRow>[] = [
        {
            id: 'day',
            header: 'Day',
            sticky: true,
            minWidth: '7rem',
            cell: (item) => <LedgerValue $em={item.dateISO === selectedISO}>{item.dateLabel}</LedgerValue>,
        },
        {
            id: 'loaded',
            header: 'Loaded',
            align: 'right',
            cell: (item) => <LedgerValue>{`${fmt(item.loadedL)} L`}</LedgerValue>,
        },
        {
            id: 'dispensed',
            header: 'Dispensed',
            align: 'right',
            cell: (item) => <LedgerValue>{`${fmt(item.dispensedL)} L`}</LedgerValue>,
        },
        {
            id: 'expected',
            header: 'Expected return',
            align: 'right',
            cell: (item) => <LedgerValue $em>{`${fmt(item.expectedReturnL)} L`}</LedgerValue>,
        },
        {
            id: 'metered',
            header: 'Metered return',
            align: 'right',
            cell: (item) => <LedgerValue>{`${fmt(item.meteredReturnL)} L`}</LedgerValue>,
        },
        {
            id: 'variance',
            header: 'Variance',
            align: 'right',
            cell: (item) => (
                <StatusPill status={item.withinTolerance ? 'success' : 'danger'}>
                    {`${signedLitres(item.varianceL)} L · ${signedPct(item.variancePct)}`}
                </StatusPill>
            ),
        },
    ]

    const housekeepingColumns: IHonestTableColumn<IFuelHousekeepingRow>[] = [
        {
            id: 'tank',
            header: 'Tank',
            sticky: true,
            minWidth: '9rem',
            cell: (item) => <LedgerValue $em>{item.tank}</LedgerValue>,
        },
        {
            id: 'lastCleaned',
            header: 'Last cleaned',
            cell: (item) => <LedgerValue>{item.lastCleaned}</LedgerValue>,
        },
        {
            id: 'nextDue',
            header: 'Next due',
            cell: (item) => <LedgerValue>{item.nextDue}</LedgerValue>,
        },
        {
            id: 'waterHistory',
            header: 'Water history',
            minWidth: '10rem',
            cell: (item) => <LedgerLabel>{item.waterHistory}</LedgerLabel>,
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item) => <StatusPill status={item.statusTone}>{item.statusLabel}</StatusPill>,
        },
    ]

    return (
        <>
            <PageWrapper>
                <NavBar defaultValue={params.q} input placeholder='Search Fuel Tanks...' setParams={setParams} />
                <Flex between mb='16' center>
                    <Flex column gap='0.25rem'>
                        <Flex center gap='0.75rem'>
                            <MainHeadline>Fuel Tanks</MainHeadline>
                        </Flex>
                        <OtherText grey>Levels, stock value and days of cover · daily dip reconciliation</OtherText>
                    </Flex>
                    <Flex center gap='0.5rem'>
                        <Button variant='secondary' onClick={() => setTransferModalOpen(true)}>Record transfer</Button>
                        {view === 'tanks' && (
                            <Button variant='primary' onClick={() => setModal({ open: true, value: null })}>Add Tank</Button>
                        )}
                    </Flex>
                </Flex>

                <Flex mb='16'>
                    <TabsContainer>
                        <Tabs active={view === 'tanks'} onClick={() => setView('tanks')}>Tanks</Tabs>
                        <Tabs active={view === 'recon'} onClick={() => setView('recon')}>Reconciliation</Tabs>
                        <Tabs active={view === 'dispensers'} onClick={() => setView('dispensers')}>Dispensers</Tabs>
                    </TabsContainer>
                </Flex>

                {view === 'tanks' && (
                    <>
                        {!vms.length ? (
                            <NoItems title='Fuel Tanks' />
                        ) : (
                            <>
                                <TanksGrid>
                                    {vms.map((vm) => (
                                        <TankCard
                                            key={vm.id}
                                            onClick={() => setModal({ open: true, value: vm.source })}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <TankInfo>
                                                <TankTopRow>
                                                    <TankName>{vm.name}</TankName>
                                                    {vm.machineClass === 'BOWSER' ? (
                                                        <StatusPill status='accent'>BOWSER</StatusPill>
                                                    ) : (
                                                        <span title={FUEL_COVER_THRESHOLD_TITLE}>
                                                            <StatusPill status={vm.tone}>{vm.statusLabel}</StatusPill>
                                                        </span>
                                                    )}
                                                </TankTopRow>

                                                <TankLevelBar
                                                    pct={vm.pct}
                                                    tone={vm.tone}
                                                    currentAmount={vm.currentAmount}
                                                    capacity={vm.capacity}
                                                    sensorPct={vm.sensorPct}
                                                    sensorAmount={vm.sensorAmount}
                                                />

                                                <TankMetricsGrid>
                                                    <div>
                                                        <TankMetricLabel>Stock value</TankMetricLabel>
                                                        <TankMetricValue $dim={!vm.stockValue}>{`GH₵ ${fmt(Math.round(vm.stockValue))}`}</TankMetricValue>
                                                    </div>
                                                    <div>
                                                        <TankMetricLabel>Burn rate</TankMetricLabel>
                                                        <TankMetricValue $dim={!vm.burnRatePerDay}>{vm.burnRatePerDay ? `${fmt(vm.burnRatePerDay)} L/day` : '—'}</TankMetricValue>
                                                    </div>
                                                </TankMetricsGrid>

                                                {vm.machineClass === 'BOWSER' ? (
                                                    <div>
                                                        <TankMetricLabel>Route</TankMetricLabel>
                                                        <CoverValue $tone='info'>{vm.routeMeta ?? '—'}</CoverValue>
                                                        <CardNote>{vm.coverNote ?? 'reloads daily · Workshop tank'}</CardNote>
                                                    </div>
                                                ) : (
                                                    <div title={FUEL_COVER_THRESHOLD_TITLE}>
                                                        <TankMetricLabel>Days of cover</TankMetricLabel>
                                                        <CoverValue $tone={vm.tone} $dim={vm.daysOfCover == null}>{fmtDays(vm.daysOfCover)}</CoverValue>
                                                    </div>
                                                )}

                                                {vm.waterCheck && (
                                                    <WaterNote $warning={vm.waterCheck.warning}>
                                                        {`Water check ${vm.waterCheck.dateLabel} · ${vm.waterCheck.result}`}
                                                    </WaterNote>
                                                )}

                                                {vm.tone === 'danger' && (
                                                    <Flex column gap='0.25rem' alignStart>
                                                        <Button
                                                            size='sm'
                                                            variant='primary'
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                toast.success(`Draft PR created for ${fmt(vm.reorderLitres)} L — review in Procurement`)
                                                            }}
                                                        >
                                                            Draft purchase request
                                                        </Button>
                                                        <CardNote>Bridges to Procurement purchase requests once connected.</CardNote>
                                                    </Flex>
                                                )}
                                            </TankInfo>
                                        </TankCard>
                                    ))}
                                </TanksGrid>
                                <TotalsLine>
                                    {`${fmt(totalLitres)} L on site · GH₵ ${fmt(Math.round(totalValue))}${totalCoverDays != null ? ` · ~${Math.round(totalCoverDays)} days at current burn` : ''}${bowserVm ? ` · +${fmt(bowserVm.currentAmount)} L on ${bowserVm.name}` : ''}`}
                                </TotalsLine>
                                <Flex mt='16' width='100%'>
                                    <Container>
                                        <ContainerHeader>
                                            <TitleText>Housekeeping</TitleText>
                                        </ContainerHeader>
                                        <ContainerContent>
                                            <HonestTable
                                                columns={housekeepingColumns}
                                                data={housekeepingRows}
                                                keyField='id'
                                                emptyMessage='Cleaning and water-check logs appear once housekeeping records are connected.'
                                            />
                                        </ContainerContent>
                                    </Container>
                                </Flex>
                            </>
                        )}
                    </>
                )}

                {view === 'dispensers' && <DispensersPanel />}

                {view === 'recon' && (
                    <>
                        {!vms.length ? (
                            <NoData text='Add a tank to start daily reconciliation.' />
                        ) : (
                            <>
                                <Flex mb='16'>
                                    <TabsContainer>
                                        <Tabs active={reconMode === 'daily'} onClick={() => setReconMode('daily')}>Daily</Tabs>
                                        <Tabs active={reconMode === 'monthend'} onClick={() => setReconMode('monthend')}>Month-end</Tabs>
                                    </TabsContainer>
                                </Flex>
                                {reconMode === 'monthend' ? (
                                    <MonthEndPanel />
                                ) : (
                                    <>
                                <Flex mb='16'>
                                    <OtherText grey>
                                        {isBowserActive
                                            ? `${activeTank?.name ?? ''} · ${fuelReconDateLabel(selectedISO)} · loaded − dispensed = expected return, checked against the metered return`
                                            : `${activeTank?.name ?? ''} · ${fuelReconDateLabel(selectedISO)} · opening dip + deliveries ± transfers − dispenses = book stock`}
                                    </OtherText>
                                </Flex>
                                <Flex mb='16' center gap='0.75rem' wrap between width='100%'>
                                    <TabsContainer>
                                        {vms.map((vm) => (
                                            <Tabs key={vm.id} active={activeTankId === vm.id} onClick={() => setReconTankId(vm.id)}>
                                                {vm.name}
                                            </Tabs>
                                        ))}
                                    </TabsContainer>
                                    <Flex center gap='0.75rem' wrap>
                                        <TabsContainer>
                                            <Tabs active={selectedISO === yesterdayISO} onClick={selectYesterday}>Yesterday</Tabs>
                                        </TabsContainer>
                                        <CalendarInput
                                            name='reconDay'
                                            placeholder='Jump to date'
                                            width='12rem'
                                            nomarg
                                            defaultValue={pickedDate}
                                            minDate={subDays(new Date(), 7)}
                                            maxDate={new Date()}
                                            onChange={handlePickDate}
                                        />
                                    </Flex>
                                </Flex>

                                {breachToday && (
                                    <BannerWrap $tone='danger'>
                                        <BannerText $tone='danger'>Variance breach — investigation required</BannerText>
                                        <Button
                                            variant='danger'
                                            size='sm'
                                            onClick={() => toast.success(`Loss record raised for ${activeTank?.name} — review in Fuel Losses`)}
                                        >
                                            Raise loss record
                                        </Button>
                                    </BannerWrap>
                                )}

                                <Flex column gap='1rem' width='100%'>
                                    <Container>
                                        <ContainerHeader>
                                            <Flex between center width='100%'>
                                                <Flex center gap='0.75rem'>
                                                    <TitleText>{`Daily reconciliation — ${activeTank?.name ?? ''}`}</TitleText>
                                                </Flex>
                                                <Flex center gap='0.5rem'>
                                                    <Button size='sm' variant='secondary' onClick={() => setTransferModalOpen(true)}>Record transfer</Button>
                                                    {!isBowserActive && (
                                                        <Button size='sm' variant='primary' onClick={() => setDipModalOpen(true)}>Enter dip</Button>
                                                    )}
                                                </Flex>
                                            </Flex>
                                        </ContainerHeader>
                                        <ContainerContent gap='0.75rem'>
                                            {isBowserActive ? (
                                                !bowserComputed ? (
                                                    <NoData text='No bowser reconciliation for this day — loads and metered returns are recorded at shift end.' />
                                                ) : (
                                                    <>
                                                        <OtherText grey>{bowserSentence(bowserComputed)}</OtherText>
                                                        <LedgerWrap>
                                                            <LedgerRow>
                                                                <LedgerOp />
                                                                <LedgerLabel>
                                                                    {`Loaded from ${bowserComputed.loadedFrom}`}
                                                                    <LedgerNote>{bowserComputed.loadedTime}</LedgerNote>
                                                                </LedgerLabel>
                                                                <LedgerValue>{`${fmt(bowserComputed.loadedL)} L`}</LedgerValue>
                                                            </LedgerRow>
                                                            <LedgerRow>
                                                                <LedgerOp>−</LedgerOp>
                                                                <LedgerLabel>
                                                                    Dispensed to machines
                                                                    <LedgerNote>{`${bowserComputed.machineCount} machines`}</LedgerNote>
                                                                </LedgerLabel>
                                                                <LedgerValue>{`${fmt(bowserComputed.dispensedL)} L`}</LedgerValue>
                                                            </LedgerRow>
                                                            <LedgerRow $em>
                                                                <LedgerOp>=</LedgerOp>
                                                                <LedgerLabel>Expected return</LedgerLabel>
                                                                <LedgerValue $em>{`${fmt(bowserComputed.expectedReturnL)} L`}</LedgerValue>
                                                            </LedgerRow>
                                                            <LedgerRow>
                                                                <LedgerOp />
                                                                <LedgerLabel>Metered return</LedgerLabel>
                                                                <LedgerValue>{`${fmt(bowserComputed.meteredReturnL)} L`}</LedgerValue>
                                                            </LedgerRow>
                                                            <VarianceRow $tone={bowserComputed.withinTolerance ? 'success' : 'danger'}>
                                                                {`Variance ${signedLitres(bowserComputed.varianceL)} L (${signedPct(bowserComputed.variancePct)}) ${bowserComputed.withinTolerance ? 'within' : 'outside'} tolerance ±${bowserTolerancePct}%`}
                                                            </VarianceRow>
                                                            {bowserComputed.note && <BreachNoteText>{bowserComputed.note}</BreachNoteText>}
                                                        </LedgerWrap>
                                                        <SignOffWrap>
                                                            {signOff ? (
                                                                <SignedText>{`✓ Signed off by ${signOff.by} · ${signOff.at}`}</SignedText>
                                                            ) : (
                                                                <>
                                                                    <OtherText grey>Reconciled by —</OtherText>
                                                                    <Button size='sm' variant='primary' onClick={handleSignOff}>Sign off</Button>
                                                                </>
                                                            )}
                                                        </SignOffWrap>
                                                        {activeTankId != null && (
                                                            <BowserDayBreakdown bowserId={activeTankId} dateISO={bowserComputed.dateISO} />
                                                        )}
                                                    </>
                                                )
                                            ) : !computed ? (
                                                <NoData text='No dip readings for this day — enter the opening and closing dips to reconcile.' />
                                            ) : (
                                                <>
                                                    <OtherText grey>{reconSentence(computed)}</OtherText>
                                                    <LedgerWrap>
                                                        <LedgerRow>
                                                            <LedgerOp />
                                                            <LedgerLabel>Opening dip</LedgerLabel>
                                                            <LedgerValue>{`${fmt(computed.openingDipL)} L`}</LedgerValue>
                                                        </LedgerRow>
                                                        <LedgerRow>
                                                            <LedgerOp>+</LedgerOp>
                                                            <LedgerLabel>Deliveries</LedgerLabel>
                                                            <LedgerValue>{`${fmt(computed.deliveriesL)} L`}</LedgerValue>
                                                        </LedgerRow>
                                                        {computed.transfers.map((line) => (
                                                            <LedgerRow key={line.id}>
                                                                <LedgerOp>{line.direction === 'TRANSFER_IN' ? '+' : '−'}</LedgerOp>
                                                                <LedgerLabel>
                                                                    {line.label}
                                                                    <LedgerNote>{line.time}</LedgerNote>
                                                                </LedgerLabel>
                                                                <LedgerValue>{`${fmt(line.litres)} L`}</LedgerValue>
                                                            </LedgerRow>
                                                        ))}
                                                        <LedgerRow>
                                                            <LedgerOp>−</LedgerOp>
                                                            <LedgerLabel>
                                                                Dispenses
                                                                {computed.dispenseCount > 0 && (
                                                                    <LinkText onClick={() => navigate('/fuel/dashboard/dispenses')}>
                                                                        {`view ${computed.dispenseCount} dispenses`}
                                                                    </LinkText>
                                                                )}
                                                            </LedgerLabel>
                                                            <LedgerValue>{`${fmt(computed.dispensesL)} L`}</LedgerValue>
                                                        </LedgerRow>
                                                        <LedgerRow $em>
                                                            <LedgerOp>=</LedgerOp>
                                                            <LedgerLabel>Book stock</LedgerLabel>
                                                            <LedgerValue $em>{`${fmt(computed.bookStockL)} L`}</LedgerValue>
                                                        </LedgerRow>
                                                        <LedgerRow>
                                                            <LedgerOp />
                                                            <LedgerLabel>Closing dip</LedgerLabel>
                                                            <LedgerValue>{`${fmt(computed.closingDipL)} L`}</LedgerValue>
                                                        </LedgerRow>
                                                        <VarianceRow $tone={computed.withinTolerance ? 'success' : 'danger'}>
                                                            {`Variance ${signedLitres(computed.varianceL)} L (${signedPct(computed.variancePct)}) ${computed.withinTolerance ? 'within' : 'outside'} tolerance ±${reconTolerancePct}%`}
                                                        </VarianceRow>
                                                    </LedgerWrap>
                                                    <SignOffWrap>
                                                        {signOff ? (
                                                            <SignedText>{`✓ Signed off by ${signOff.by} · ${signOff.at}`}</SignedText>
                                                        ) : (
                                                            <>
                                                                <OtherText grey>Reconciled by —</OtherText>
                                                                <Button size='sm' variant='primary' onClick={handleSignOff}>Sign off</Button>
                                                            </>
                                                        )}
                                                    </SignOffWrap>
                                                </>
                                            )}
                                        </ContainerContent>
                                    </Container>

                                    <Container>
                                        <ContainerHeader>
                                            <TitleText>{`Last 7 days — ${activeTank?.name ?? ''}`}</TitleText>
                                        </ContainerHeader>
                                        <ContainerContent>
                                            {isBowserActive ? (
                                                <HonestTable
                                                    columns={bowserHistoryColumns}
                                                    data={bowserHistoryRows}
                                                    keyField='id'
                                                    emptyMessage='No bowser reconciliation history yet.'
                                                    onClick={(item) => {
                                                        setSelectedISO(item.dateISO)
                                                        setPickedDate(null)
                                                    }}
                                                />
                                            ) : (
                                                <HonestTable
                                                    columns={historyColumns}
                                                    data={historyRows}
                                                    keyField='id'
                                                    emptyMessage='No reconciliation history yet.'
                                                    onClick={(item) => {
                                                        setSelectedISO(item.dateISO)
                                                        setPickedDate(null)
                                                    }}
                                                />
                                            )}
                                        </ContainerContent>
                                    </Container>
                                </Flex>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}

                <FootnoteLine>{FUEL_THRESHOLD_FOOTNOTE}</FootnoteLine>
            </PageWrapper>

            {modal.open && (
                <TanksModal value={modal.value} closeModal={() => setModal({ value: null, open: false })} />
            )}

            {dipModalOpen && (
                <EnterDipModal
                    closeModal={() => setDipModalOpen(false)}
                    tankOptions={dipTankOptions}
                    defaultTankId={isBowserActive ? null : activeTankId}
                    defaultDateISO={selectedISO}
                    onSave={handleDipSave}
                />
            )}

            {transferModalOpen && (
                <TransferModal
                    closeModal={() => setTransferModalOpen(false)}
                    tankOptions={transferTankOptions}
                    defaultFromId={isBowserActive ? null : activeTankId}
                    onSave={handleTransferSave}
                />
            )}

            {view === 'tanks' && (
                <PageFooter>
                    <Pagination
                        setPage={(page) => handlePagination(page, setParams)}
                        pageCount={Math.ceil(tanks.totalPages)}
                        forcePage={(params.page ? params.page : 1) - 1}
                    />
                </PageFooter>
            )}
        </>
    )
}
